import { startOfMonth, endOfMonth, subDays, startOfDay } from 'date-fns';

// Types.
import { InvoiceAkamon, Ledger } from './types/billing';
import { Config } from './types/config';
import { CampaignPriceOffer, Contract, ContractBasicData, WeatherDataV2 } from './types/contract';
import { ConsumptionAverage, ConsumptionRates, SelectedMeteringPoint, WeightedAvgSpotPriceItem } from './types/consumptionRates';
import { HubSpotBlog } from './types/news';
import { AvailableUsers } from './types/customer';
// Utils.
import { initialUsers } from './utils/currentPage';

import lummectx from './LummeContext';
import { NordPoolSpotGraphDatapoint, ReportingGraphDatapoint } from './types/graphs';
import { EliqEuc, EliqForecast, EliqHomeProfile, EliqSimilarHomes, EliqSimilarHomesConsumption, EliqSimilarHomesReport } from './types/eliq';
import { EmablerDataUI, EvChargingStatus } from './types/evCharging';

const initialLedger = {
  contractCode: '',
  ledgerNo: 0,
  contractNo: 0,
};

export type ConsumptionEliq = {
  homeProfileCompletion?: number;
  homeProfile?: EliqHomeProfile;
  euc?: EliqEuc;
  similarHomes?: EliqSimilarHomes;
  similarHomesConsumption?: EliqSimilarHomesConsumption;
  similarHomesReport?: EliqSimilarHomesReport;
};

export type ConsumptionPage = {
  activeFilter: string;
  dataLoading: boolean;
  startDate: Date | undefined;
  endDate: Date | undefined;
  type: 'single' | 'group';
  consumption?: ConsumptionRates;
  eliqForecast?: EliqForecast;
  weather?: WeatherDataV2;
  nordPoolData?: NordPoolSpotGraphDatapoint[];
  graphData?: ReportingGraphDatapoint[];
  weightedAvg?: WeightedAvgSpotPriceItem[];
  consumptionAvg?: ConsumptionAverage;
};

export const initialConsumptionPage: ConsumptionPage = {
  activeFilter: 'month',
  dataLoading: true,
  startDate: startOfMonth(new Date()),
  endDate: endOfMonth(new Date()),
  type: 'single',
  consumption: undefined,
  weather: undefined,
  nordPoolData: undefined,
  graphData: undefined,
  weightedAvg: undefined,
  consumptionAvg: undefined,
};

export type LandingPage = {
  currentMonthConsumption?: number;
  lastYearConsumption?: number;
  spotPricesToday?: NordPoolSpotGraphDatapoint[];
};

export const initialLandingPage: LandingPage = {
  currentMonthConsumption: undefined,
  lastYearConsumption: undefined,
  spotPricesToday: undefined,
};

export type Maintenance = {
  active: boolean;
  message: string;
};

type AppState = {
  maintenance?: Maintenance;
  config?: Config;
  basicData?: ContractBasicData[];
  loadingBasicData: boolean;
  contracts?: Contract[];
  loadingContracts: boolean;
  invoices?: InvoiceAkamon[];
  invoicesNextPageToken: string | null;
  ledgers?: Ledger[];
  landingPage: LandingPage;
  news: HubSpotBlog[];
  offers: CampaignPriceOffer[];
  selectedMeteringPoint?: SelectedMeteringPoint;
  selectedMeteringPointContract?: Contract;
  users: AvailableUsers;
};

type AppAction =
  | ['changeMaintenance', Maintenance]
  | ['changeConfig', Config]
  | ['changeBasicData', ContractBasicData[] | undefined]
  | ['changeLoadingBasicData', boolean]
  | ['changeContracts', Contract[] | undefined]
  | ['changeLoadingContracts', boolean]
  | ['changeInvoices', InvoiceAkamon[] | undefined]
  | ['changeInvoicesNextPageToken', string | null]
  | ['changeLedgers', Ledger[] | undefined]
  | ['changeLandingPage', LandingPage]
  | ['changeLandingPageSpotPricesToday', NordPoolSpotGraphDatapoint[] | undefined]
  | ['changeNews', HubSpotBlog[]]
  | ['changeOffers', CampaignPriceOffer[]]
  | ['changeSelectedMeteringPoint', SelectedMeteringPoint | undefined]
  | ['changeSelectedMeteringPointContract', Contract | undefined]
  | ['changeUsers', AvailableUsers];

function appStateReducer(state: AppState, action: AppAction) {
  switch (action[0]) {
    case 'changeMaintenance':
      return { ...state, maintenance: action[1] };
    case 'changeConfig':
      return { ...state, config: action[1] };
    case 'changeBasicData':
      return { ...state, basicData: action[1] };
    case 'changeLoadingBasicData':
      return { ...state, loadingBasicData: action[1] };
    case 'changeContracts':
      return { ...state, contracts: action[1] };
    case 'changeLoadingContracts':
      return { ...state, loadingContracts: action[1] };
    case 'changeInvoices':
      return { ...state, invoices: action[1] };
    case 'changeInvoicesNextPageToken':
      return { ...state, invoicesNextPageToken: action[1] };
    case 'changeLedgers':
      return { ...state, ledgers: action[1] };

    // Landing page starts
    case 'changeLandingPage':
      return { ...state, landingPage: action[1] };
    case 'changeLandingPageSpotPricesToday':
      return { ...state, landingPage: { ...state.landingPage, spotPricesToday: action[1] } };
    // Landing page ends

    case 'changeNews':
      return { ...state, news: action[1] };
    case 'changeOffers':
      return { ...state, offers: action[1] };
    case 'changeSelectedMeteringPoint':
      return { ...state, selectedMeteringPoint: action[1] };
    case 'changeSelectedMeteringPointContract':
      return { ...state, selectedMeteringPointContract: action[1] };
    case 'changeUsers':
      return { ...state, users: action[1] };
  }
}

const OmaLummeApp = lummectx(appStateReducer, () => ({
  maintenance: undefined,
  config: undefined,
  basicData: undefined,
  loadingBasicData: false,
  contracts: undefined,
  loadingContracts: false,
  invoices: undefined,
  invoicesNextPageToken: null,
  ledgers: [],
  landingPage: initialLandingPage,
  news: [],
  offers: [],
  retLedger: initialLedger,
  selectedMeteringPoint: undefined,
  selectedMeteringPointContract: undefined,
  users: initialUsers,
}));

const OmaLummeProvider = OmaLummeApp.Provider;

export { OmaLummeApp, OmaLummeProvider };

type ConsumptionPageState = {
  activeFilter: string;
  dataLoading: boolean;
  startDate: Date | undefined;
  endDate: Date | undefined;
  type: string;
  consumption?: ConsumptionRates;
  eliqForecast?: EliqForecast;
  weather?: WeatherDataV2;
  nordPoolData?: NordPoolSpotGraphDatapoint[];
  graphData?: ReportingGraphDatapoint[];
  weightedAvg?: WeightedAvgSpotPriceItem[];
  consumptionAvg?: ConsumptionAverage;

  homeProfileCompletion?: number;
  homeProfile?: EliqHomeProfile;
  euc?: EliqEuc;
  similarHomes?: EliqSimilarHomes;
  similarHomesConsumption?: EliqSimilarHomesConsumption;
  similarHomesReport?: EliqSimilarHomesReport;
  latestDay: Date | undefined;
  latestDayFetched: boolean;
  contractIsNoPrice: boolean;
  consumptionAsMoney: boolean;
  dayConsumptionAs15min: boolean;
  showComparisonData: boolean;
  totalConsumption: number;
  previousConsumption: number;
  lastYearConsumption: number;
  consumptionEstimate: number;
};

type ConsumptionPageAction =
  | ['changeActiveFilter', string]
  | ['changeDataLoading', boolean]
  | ['changeStartDate', Date | undefined]
  | ['changeEndDate', Date | undefined]
  | ['changeType', 'single' | 'group']
  | ['changeConsumption', ConsumptionRates | undefined]
  | ['changeForecast', EliqForecast | undefined]
  | ['changeWeather', WeatherDataV2 | undefined]
  | ['changeSpot', NordPoolSpotGraphDatapoint[] | undefined]
  | ['changeWeightedAvg', WeightedAvgSpotPriceItem[] | undefined]
  | ['changeConsumptionAvg', ConsumptionAverage | undefined]
  | ['changeGraphData', ReportingGraphDatapoint[] | undefined]
  | ['changeEliqProfileCompletion', number | undefined]
  | ['changeEliqProfile', EliqHomeProfile | undefined]
  | ['changeEliqEuc', EliqEuc | undefined]
  | ['changeEliqSimilarHomes', EliqSimilarHomes | undefined]
  | ['changeEliqSimilarHomesConsumption', EliqSimilarHomesConsumption | undefined]
  | ['changeEliqSimilarHomesReport', EliqSimilarHomesReport | undefined]
  | ['changeLatestDay', Date | undefined]
  | ['changeLatestDayFetched', boolean]
  | ['changeContractIsNoPrice', boolean]
  | ['changeConsumptionAsMoney', boolean]
  | ['changeDayConsumptionAs15min', boolean]
  | ['changeShowComparisonData', boolean]
  | ['changeTotalConsumption', number]
  | ['changePreviousConsumption', number]
  | ['changeLastYearConsumption', number]
  | ['changeConsumptionEstimate', number];

function consumptionPageStateReducer(state: ConsumptionPageState, action: ConsumptionPageAction) {
  switch (action[0]) {
    case 'changeActiveFilter':
      return { ...state, activeFilter: action[1] };
    case 'changeDataLoading':
      return { ...state, dataLoading: action[1] };
    case 'changeStartDate':
      return { ...state, startDate: action[1] };
    case 'changeEndDate':
      return { ...state, endDate: action[1] };
    case 'changeType':
      return { ...state, type: action[1] };

    case 'changeConsumption':
      return { ...state, consumption: action[1] };
    case 'changeForecast':
      return { ...state, eliqForecast: action[1] };
    case 'changeWeather':
      return { ...state, weather: action[1] };
    case 'changeSpot':
      return { ...state, nordPoolData: action[1] };
    case 'changeWeightedAvg':
      return { ...state, weightedAvg: action[1] };
    case 'changeConsumptionAvg':
      return { ...state, consumptionAvg: action[1] };
    case 'changeGraphData':
      return { ...state, graphData: action[1] };

    case 'changeEliqProfileCompletion':
      return { ...state, homeProfileCompletion: action[1] };
    case 'changeEliqProfile':
      return { ...state, homeProfile: action[1] };
    case 'changeEliqEuc':
      return { ...state, euc: action[1] };
    case 'changeEliqSimilarHomes':
      return { ...state, similarHomes: action[1] };
    case 'changeEliqSimilarHomesConsumption':
      return { ...state, similarHomesConsumption: action[1] };
    case 'changeEliqSimilarHomesReport':
      return { ...state, similarHomesReport: action[1] };

    case 'changeLatestDay':
      return { ...state, latestDay: action[1] };
    case 'changeLatestDayFetched':
      return { ...state, latestDayFetched: action[1] };
    case 'changeContractIsNoPrice':
      return { ...state, contractIsNoPrice: action[1] };
    case 'changeConsumptionAsMoney':
      return { ...state, consumptionAsMoney: action[1] };
    case 'changeDayConsumptionAs15min':
      return { ...state, dayConsumptionAs15min: action[1] };
    case 'changeShowComparisonData':
      return { ...state, showComparisonData: action[1] };
    case 'changeTotalConsumption':
      return { ...state, totalConsumption: action[1] };
    case 'changePreviousConsumption':
      return { ...state, previousConsumption: action[1] };
    case 'changeLastYearConsumption':
      return { ...state, lastYearConsumption: action[1] };
    case 'changeConsumptionEstimate':
      return { ...state, consumptionEstimate: action[1] };
  }
}

const ConsumptionApp = lummectx(consumptionPageStateReducer, () => ({
  activeFilter: 'month',
  dataLoading: true,
  startDate: startOfMonth(new Date()),
  endDate: endOfMonth(new Date()),
  type: 'single',
  consumption: undefined,
  weather: undefined,
  nordPoolData: undefined,
  graphData: undefined,
  weightedAvg: undefined,
  consumptionAvg: undefined,

  homeProfileCompletion: undefined,
  homeProfile: undefined,
  euc: undefined,
  similarHomes: undefined,
  similarHomesConsumption: undefined,
  similarHomesReport: undefined,

  latestDay: subDays(startOfDay(new Date()), 3),
  latestDayFetched: false,
  contractIsNoPrice: false,
  consumptionAsMoney: false,
  dayConsumptionAs15min: false,
  showComparisonData: false,
  totalConsumption: 0,
  previousConsumption: 0,
  lastYearConsumption: 0,
  consumptionEstimate: 0,
}));

const ConsumptionProvider = ConsumptionApp.Provider;

export { ConsumptionApp, ConsumptionProvider };

export type EvChargingState = {
  evChargerInfo?: EmablerDataUI;
  evChargerInfoLoading: boolean;
  selectedCharger: string;
  chargerStatus?: EvChargingStatus;
  activeSite: string;
  activeTag: string;
};

type EvChargingAction =
  | ['changeEvChargerInfo', EmablerDataUI | undefined]
  | ['changeEvChargerInfoLoading', boolean]
  | ['changeSelectedCharger', string]
  | ['changeChargerStatus', EvChargingStatus | undefined]
  | ['changeActiveTag', string]
  | ['changeActiveSite', string];

function evChargingStateReducer(state: EvChargingState, action: EvChargingAction) {
  switch (action[0]) {
    case 'changeEvChargerInfo':
      return { ...state, evChargerInfo: action[1] };
    case 'changeEvChargerInfoLoading':
      return { ...state, evChargerInfoLoading: action[1] };
    case 'changeSelectedCharger':
      return { ...state, selectedCharger: action[1] };
    case 'changeChargerStatus':
      return { ...state, chargerStatus: action[1] };
    case 'changeActiveTag':
      return { ...state, activeTag: action[1] };
    case 'changeActiveSite':
      return { ...state, activeSite: action[1] };
  }
}

const EvChargingApp = lummectx(evChargingStateReducer, () => ({
  evChargerInfo: undefined,
  evChargerInfoLoading: false,
  selectedCharger: window.localStorage.getItem('EvChargingChargerSocket') || '',
  chargerStatus: undefined,
  activeSite: '',
  activeTag: '',
}));

const EvChargingProvider = EvChargingApp.Provider;

export { EvChargingApp, EvChargingProvider };
